<template>
  <div :class="wrapperClasses">
    <img v-if="src" :src="src" :alt="alt" :class="imgClasses" @error="onErr" />
    <span v-else :class="initialsClasses" :style="gradientStyle">
      {{ computedInitials }}
    </span>
    <span v-if="status" :class="statusClasses" :title="statusTitle" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  src: String,
  alt: { type: String, default: 'Avatar' },
  name: String,
  firstname: String,
  lastname: String,
  initials: String,
  size: { type: String, default: 'md' }, // xs, sm, md, lg, xl
  status: { type: String, default: '' }, // 'online' | 'offline' | 'busy' | ''
  seed: String
})

const imgErr = ref(false)
const onErr = () => {
  imgErr.value = true
}

const sizeMap = {
  xs: {
    wrap: 'w-6 h-6',
    text: 'text-2xs',
    dot: 'w-1.5 h-1.5 -bottom-0 -right-0',
    border: 'border'
  },
  sm: { wrap: 'w-8 h-8', text: 'text-xs', dot: 'w-2 h-2 -bottom-0 -right-0', border: 'border-2' },
  md: {
    wrap: 'w-10 h-10',
    text: 'text-sm',
    dot: 'w-3 h-3 -bottom-0.5 -right-0.5',
    border: 'border-2'
  },
  lg: {
    wrap: 'w-12 h-12',
    text: 'text-base',
    dot: 'w-3.5 h-3.5 -bottom-0.5 -right-0.5',
    border: 'border-2'
  },
  xl: { wrap: 'w-16 h-16', text: 'text-xl', dot: 'w-4 h-4 -bottom-0 -right-0', border: 'border-2' }
}

// Palette de gradients déterministes — visuellement plaisants
const palettes = [
  ['#2F66E3', '#14B8A6'], // brand → accent
  ['#5E89EE', '#3DD3C0'],
  ['#8B5CF6', '#2F66E3'], // violet → brand
  ['#0D9488', '#3DD3C0'],
  ['#F59E0B', '#FB7185'], // amber → rose
  ['#EC4899', '#8B5CF6'], // pink → violet
  ['#06B6D4', '#2F66E3'], // cyan → brand
  ['#10B981', '#14B8A6'],
  ['#F43F5E', '#F97316'],
  ['#7C3AED', '#EC4899']
]

const hashStr = (s) => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const computedInitials = computed(() => {
  if (props.initials) return props.initials.slice(0, 2).toUpperCase()
  const f = (props.firstname || '').trim()
  const l = (props.lastname || '').trim()
  if (f || l) return `${f[0] || ''}${l[0] || ''}`.toUpperCase() || '?'
  const n = (props.name || '').trim()
  if (n) {
    const parts = n.split(/\s+/)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return n.slice(0, 2).toUpperCase()
  }
  return '?'
})

const gradientStyle = computed(() => {
  if (props.src && !imgErr.value) return {}
  const key =
    props.seed ||
    `${props.firstname || ''}${props.lastname || ''}${props.name || ''}${computedInitials.value}`
  const [a, b] = palettes[hashStr(key) % palettes.length]
  return { background: `linear-gradient(135deg, ${a}, ${b})` }
})

const wrapperClasses = computed(() => [
  'relative inline-flex flex-shrink-0',
  sizeMap[props.size]?.wrap || sizeMap.md.wrap
])

const imgClasses = computed(() => ['w-full h-full rounded-full object-cover'])

const initialsClasses = computed(() => [
  'w-full h-full rounded-full flex items-center justify-center text-white font-semibold',
  sizeMap[props.size]?.text || sizeMap.md.text
])

const statusMap = {
  online: 'bg-accent-500',
  offline: 'bg-ink-300',
  busy: 'bg-amber-500',
  away: 'bg-amber-400'
}
const statusTitles = {
  online: 'En ligne',
  offline: 'Hors ligne',
  busy: 'Occupé',
  away: 'Absent'
}
const statusClasses = computed(() => [
  'absolute rounded-full border-white',
  statusMap[props.status] || 'bg-ink-300',
  sizeMap[props.size]?.dot || sizeMap.md.dot,
  sizeMap[props.size]?.border || 'border-2'
])
const statusTitle = computed(() => statusTitles[props.status] || '')
</script>
